import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageSquare, Clock, ChevronRight, Lock, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ThreadPage() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchThread = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/forum/thread/${threadId}?page=${page}`);
        if (response.ok) {
          const data = await response.json();
          setThread(data.thread);
          setPosts(data.posts);
          setTotalPages(data.pages);
        }
      } catch (error) {
        console.error('Failed to fetch thread:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchThread();
  }, [threadId, page]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Login to reply');
      return;
    }

    if (!replyContent.trim()) {
      toast.error('Reply cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/forum/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          thread_id: threadId,
          content: replyContent
        })
      });

      if (response.ok) {
        const newPost = await response.json();
        setPosts(prev => [...prev, newPost]);
        setReplyContent('');
        toast.success('Reply posted!');
        setThread(prev => ({ ...prev, reply_count: prev.reply_count + 1 }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post reply');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="thread-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#666]">
        <Link to="/bbs" className="hover:text-[#ffcc00]">BBS</Link>
        <ChevronRight size={12} />
        <span className="text-[#999]">{thread?.title || 'Loading...'}</span>
      </div>

      {/* Thread header */}
      {thread && (
        <div className="bg-[#111] border border-[#333] p-4">
          <div className="flex items-start gap-3">
            {thread.is_locked && <Lock size={20} className="text-[#666] mt-1" />}
            <div>
              <h1 className="text-xl font-black text-white">{thread.title}</h1>
              <div className="text-xs text-[#666] mt-1 flex items-center gap-4">
                <span>by <span className="text-[#ffcc00]">{thread.author_name}</span></span>
                <span className="flex items-center gap-1">
                  <Clock size={10} />
                  {formatDate(thread.created_at)}
                </span>
                <span>{thread.reply_count} replies</span>
                <span>{thread.view_count} views</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Posts */}
      <div className="space-y-0 border border-[#333]" data-testid="posts-list">
        {loading ? (
          Array(3).fill(0).map((_, i) => (
            <div key={i} className="ws-post animate-pulse">
              <div className="ws-post-author">
                <div className="w-16 h-16 bg-[#1a1a1a] mx-auto mb-2" />
                <div className="h-3 w-20 bg-[#1a1a1a] mx-auto" />
              </div>
              <div className="ws-post-content">
                <div className="h-4 w-3/4 bg-[#1a1a1a] mb-2" />
                <div className="h-4 w-1/2 bg-[#1a1a1a]" />
              </div>
            </div>
          ))
        ) : posts.map((post, index) => (
          <div 
            key={post.post_id} 
            className="ws-post"
            data-testid={`post-${post.post_id}`}
          >
            {/* Author sidebar */}
            <div className="ws-post-author">
              <Link to={`/user/${post.author_id}`}>
                {post.author_avatar ? (
                  <img 
                    src={post.author_avatar.startsWith('/') 
                      ? `${BACKEND_URL}${post.author_avatar}` 
                      : post.author_avatar}
                    alt=""
                    className="w-16 h-16 mx-auto mb-2 border border-[#333]"
                  />
                ) : (
                  <div className="w-16 h-16 mx-auto mb-2 bg-[#1a1a1a] border border-[#333] flex items-center justify-center">
                    <User size={24} className="text-[#444]" />
                  </div>
                )}
                <div className="text-sm font-bold text-[#ffcc00] hover:text-[#ffd700]">
                  {post.author_name}
                </div>
              </Link>
              <div className="text-[10px] text-[#666] mt-1 uppercase">Member</div>
            </div>

            {/* Post content */}
            <div className="ws-post-content">
              <div className="text-xs text-[#666] mb-3 flex items-center gap-1">
                <Clock size={10} />
                {formatDate(post.created_at)}
                {index === 0 && <span className="ml-2 text-[#cc0000]">[OP]</span>}
              </div>
              <div className="text-sm text-[#ccc] whitespace-pre-wrap">
                {post.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary text-xs disabled:opacity-50"
          >
            PREV
          </button>
          <span className="text-xs text-[#666] mono px-4">
            PAGE {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary text-xs disabled:opacity-50"
          >
            NEXT
          </button>
        </div>
      )}

      {/* Reply form */}
      {thread && !thread.is_locked && (
        <div className="bg-[#111] border border-[#333] p-4" data-testid="reply-form">
          <h3 className="text-sm font-bold text-white mb-3">POST REPLY</h3>
          <form onSubmit={handleReply}>
            <Textarea
              placeholder={user ? "Type your reply..." : "Login to reply"}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              disabled={!user}
              className="bg-black border-[#333] text-white min-h-[100px] mb-3"
              data-testid="reply-textarea"
            />
            <button
              type="submit"
              disabled={!user || submitting}
              className="btn-primary text-xs disabled:opacity-50"
              data-testid="reply-submit"
            >
              {submitting ? 'POSTING...' : 'POST REPLY'}
            </button>
          </form>
        </div>
      )}

      {thread?.is_locked && (
        <div className="bg-[#1a1a00] border border-[#333300] p-4 text-center">
          <Lock size={20} className="mx-auto mb-2 text-[#999]" />
          <p className="text-xs text-[#999]">This thread is locked. No new replies allowed.</p>
        </div>
      )}
    </div>
  );
}
